import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { exchangeToken, parseCallback, readEnv } from "../auth/oauth";
import { exchangeTokenViaBackend } from "../services/backend/sfAuth";
import { useAuth } from "../auth/AuthContext";
import { storage } from "../auth/storage";
import { getEnv } from "../config/getEnv";
import { syncAzureTokenCookies } from "../services/backend/azureAuth";
import {
  isAzureLoginEnabled,
  isSfLoginEnabled,
  getMsalInstance,
  getAzureLoginScopes,
} from "../auth/azureConfig";
import { Spin } from "antd";

function CallbackSpinner() {
  return (
    <main
      style={{
        padding: 24,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 200,
      }}
    >
      <Spin tip="Processing sign-in…" />
    </main>
  );
}

/**
 * Handles /azure/callback. Lets MSAL process the redirect, then redirects to /login
 * so the user can continue with Salesforce (or home if only Azure is required).
 */
function AzureCallbackHandler() {
  const nav = useNavigate();
  const { instance } = useMsal();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const run = async () => {
      try {
        const url = new URL(window.location.href);
        const errorParam =
          url.searchParams.get("error") ||
          url.hash?.match(/error=([^&]+)/)?.[1];
        if (errorParam) {
          handled.current = true;
          nav(`/login?error=${encodeURIComponent(errorParam)}`, {
            replace: true,
          });
          return;
        }
        await instance.initialize();
        const result = await instance.handleRedirectPromise();
        if (result?.account) {
          const scopes = getAzureLoginScopes();
          const tokenResult = await instance.acquireTokenSilent({
            scopes,
            account: result.account,
          });
          const beUrl = getEnv("REACT_APP_BE_URL");
          if (beUrl) {
            try {
              await syncAzureTokenCookies({
                access_token: tokenResult.accessToken,
                id_token: tokenResult.idToken ?? undefined,
              });
            } catch {
              /* cookie sync failed */
            }
          }
          storage.saveAzureAuthSession({
            backendSessionCookie: Boolean(beUrl),
            backendCookieExpiresAt:
              beUrl && tokenResult.expiresOn
                ? tokenResult.expiresOn.getTime()
                : undefined,
            tokenSet: beUrl
              ? {
                  id_token: tokenResult.idToken ?? undefined,
                  issued_at: Date.now(),
                }
              : {
                  access_token: tokenResult.accessToken,
                  id_token: tokenResult.idToken ?? undefined,
                  issued_at: Date.now(),
                },
            account: result.account
              ? {
                  username: result.account.username,
                  localAccountId: result.account.localAccountId,
                  name: result.account.name,
                }
              : undefined,
            issuedAt: Date.now(),
          });
        }
        handled.current = true;
        nav("/login", { replace: true });
      } catch (e) {
        console.error("Azure callback error", e);
        handled.current = true;
        nav("/login?error=token_exchange_failed", { replace: true });
      }
    };
    run();
  }, [instance, nav]);

  return <CallbackSpinner />;
}

/**
 * Handles /callback (Salesforce OAuth). Exchanges code for token, logs access_token, redirects.
 */
function SalesforceCallbackHandler() {
  const nav = useNavigate();
  const loc = useLocation();
  const { applySfTokenSet } = useAuth();

  useEffect(() => {
    const run = async () => {
      if (!isSfLoginEnabled()) {
        nav("/login", { replace: true });
        return;
      }
      const env = readEnv();
      try {
        const maybeErrorUrl = new URL(window.location.href);
        if (maybeErrorUrl.searchParams.has("error")) {
          maybeErrorUrl.searchParams.delete("error");
          window.history.replaceState(
            {},
            document.title,
            maybeErrorUrl.toString(),
          );
        }
      } catch {}
      const result = parseCallback(window.location.href);
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, document.title, url.toString());

      if (result.type === "error") {
        if (result.error === "invalid_callback") {
          nav("/login", { replace: true });
          return;
        }
        if (result.error === "login_required") {
          nav("/login", { replace: true });
          return;
        }
        nav(`/login?error=${encodeURIComponent(result.error)}`, {
          replace: true,
        });
        return;
      }
      const temp = storage.readTemp();
      if (!temp || temp.state !== result.state) {
        nav("/login?error=state_mismatch", { replace: true });
        return;
      }
      try {
        const backendUrl = getEnv("REACT_APP_BE_URL");
        let tokenSet;
        if (backendUrl) {
          tokenSet = await exchangeTokenViaBackend({
            code: result.code,
            code_verifier: temp.codeVerifier,
            redirect_uri: env.SF_REDIRECT_URI,
            auth_domain: env.SF_AUTH_DOMAIN,
            client_id: env.SF_CLIENT_ID,
          });
        } else {
          tokenSet = await exchangeToken(env, result.code);
        }
        if (tokenSet?.access_token) {
          applySfTokenSet({
            ...tokenSet,
            sfAccessToken: tokenSet.access_token,
          });
        }
        storage.clearTemp();
        nav("/", { replace: true });
      } catch (e) {
        nav("/login?error=token_exchange_failed", { replace: true });
      }
    };
    run();
  }, [loc]);

  return <CallbackSpinner />;
}

export default function Callback() {
  const loc = useLocation();
  const pathname = loc.pathname;
  const handleAzureCallback = isAzureLoginEnabled() && getMsalInstance();

  if (pathname === "/azure/callback") {
    if (handleAzureCallback) return <AzureCallbackHandler />;
    return <Navigate to="/login" replace />;
  }
  if (pathname === "/callback") {
    return <SalesforceCallbackHandler />;
  }

  return <CallbackSpinner />;
}
